(function () {
  const productSelector = '.product[data-section-type="product"]:not(.product-footer)';
  const mediaContainerSelector = '.product__media-container';
  const lightboxSelector = '[data-lightbox]';
  const mediaWrapperSelector = '[data-product-media-wrapper]';
  const thumbnailSelector = '[data-product-thumbnail]';
  const progressiveImageSelector = 'img[data-al-progressive-image]';
  const quickPurchaseImageSelector = '.quick-purchase-bar__product-image';
  const primaryImageDecodeTimeout = 450;
  const responsiveWidths = [360, 540, 720, 900, 1080, 1296, 1512, 1728, 2048];

  let activeRequest = null;
  let lastRequestedVariantId = null;
  let lastRenderedVariantId = null;
  const mediaCache = new Map();
  const mediaGroupCache = new Map();
  let productDataPromise = null;
  let productMediaIndex = null;
  let productDataFailed = false;
  let renderSequence = 0;
  let mainMediaTemplate = null;
  let thumbnailTemplate = null;
  let lastRenderedGroupKey = '';
  let activeInteractiveMedia = null;
  let managedLightbox = null;
  let managedLightboxIndex = 0;
  let managedLightboxLastFocus = null;
  let managedLightboxTouchStartX = null;
  let lockedDocumentOverflow = '';
  let lockedBodyOverflow = '';

  function getProductContainer() {
    return document.querySelector(productSelector);
  }

  function getCurrentVariantId(product) {
    const variantSelect = product && product.querySelector('[data-variant-select]');
    return variantSelect && variantSelect.value ? String(variantSelect.value) : '';
  }

  function getMediaId(variant) {
    return variant && variant.featured_media && variant.featured_media.id
      ? String(variant.featured_media.id)
      : '';
  }

  function hasMedia(product, mediaId) {
    return !!product.querySelector(`[data-media-item-id="${mediaId}"]:not([data-al-media-placeholder])`);
  }

  /* Bedding Clearance start */
  function isBeddingClearanceProduct(product) {
    return !!product && product.classList.contains('product--bedding-clearance');
  }
  /* Bedding Clearance end */

  function canAttemptFastPath(product) {
    return !!(
      product &&
      product.dataset.productUrl &&
      product.dataset.productLightboxEnabled !== 'true' &&
      product.dataset.mobileGalleryStyle !== 'slider' &&
      !isBeddingClearanceProduct(product) &&
      product.querySelector('.product__media') &&
      !product.querySelector('.product__media-slider')
    );
  }

  function getProductDataUrl(product) {
    const url = new URL(product.dataset.productUrl, window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = url.pathname.replace(/\.js$/, '') + '.js';
    return url.toString();
  }

  function buildProductMediaIndex(data) {
    if (!data || !Array.isArray(data.options) || !Array.isArray(data.variants) || !Array.isArray(data.media)) {
      return null;
    }

    const colourOptionIndex = data.options.findIndex((option) => {
      const name = typeof option === 'string' ? option : option && option.name;
      return /^(color|colour)$/i.test(String(name || '').trim());
    });

    if (colourOptionIndex < 0) return null;

    const groupStarts = new Map();
    const seenGroups = new Set();

    data.variants.forEach((variant) => {
      const groupValue = variant.options && variant.options[colourOptionIndex];
      const featuredMediaId = getMediaId(variant);
      const groupKey = String(groupValue == null ? '' : groupValue);

      if (!groupKey || !featuredMediaId || seenGroups.has(groupKey)) return;

      seenGroups.add(groupKey);
      groupStarts.set(featuredMediaId, groupKey);
    });

    const groups = new Map();
    let activeGroup = '';

    data.media.forEach((media) => {
      const groupStart = groupStarts.get(String(media.id));
      if (groupStart) activeGroup = groupStart;
      if (!activeGroup) return;

      if (!groups.has(activeGroup)) groups.set(activeGroup, []);
      groups.get(activeGroup).push(media);
    });

    return {
      colourOptionIndex,
      groups
    };
  }

  function loadProductMediaIndex() {
    const product = getProductContainer();
    if (!canAttemptFastPath(product)) return Promise.resolve(null);
    if (productDataFailed) return Promise.resolve(null);
    if (productMediaIndex) return Promise.resolve(productMediaIndex);
    if (productDataPromise) return productDataPromise;

    productDataPromise = fetch(getProductDataUrl(product), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product JSON request failed: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        productMediaIndex = buildProductMediaIndex(data);
        if (!productMediaIndex) throw new Error('Product JSON did not include a supported Colour media structure');
        return productMediaIndex;
      })
      .catch((error) => {
        productDataFailed = true;
        productDataPromise = null;
        console.warn('[PDP media fast path]', error);
        return null;
      });

    return productDataPromise;
  }

  function prefetchProductMediaIndex() {
    const product = getProductContainer();
    if (!canAttemptFastPath(product)) return;

    const load = () => loadProductMediaIndex();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(load, { timeout: 800 });
    } else {
      window.setTimeout(load, 250);
    }
  }

  function getCdnImageUrl(source, width) {
    if (!source) return '';

    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', width);
    return url.toString();
  }

  function getResponsiveSrcset(source, widths) {
    if (!source) return '';

    return widths
      .map((width) => `${getCdnImageUrl(source, width)} ${width}w`)
      .join(', ');
  }

  function updateQuickPurchaseImage(product, variant) {
    const preview = variant && variant.featured_media && variant.featured_media.preview_image;
    const wrapper = product && product.querySelector(quickPurchaseImageSelector);
    const image = wrapper && wrapper.querySelector('img');

    if (!preview || !preview.src || !image) return;

    image.className = 'image__img';
    image.src = getCdnImageUrl(preview.src, 128);
    image.srcset = getResponsiveSrcset(preview.src, [64, 128, 192]);
    image.sizes = '64px';
    image.alt = preview.alt || variant.name || '';
    image.width = preview.width || 1;
    image.height = preview.height || 1;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.removeAttribute('data-src');
    image.removeAttribute('data-srcset');
    image.removeAttribute('data-sizes');
    image.removeAttribute('data-widths');
  }

  function prepareImage(image, media, thumbnail) {
    const preview = media.preview_image || media;
    const source = media.src || preview.src;
    const placeholderWidth = thumbnail ? 80 : 360;
    const widths = thumbnail ? [70, 140, 210] : responsiveWidths;
    const sizes = thumbnail ? '70px' : '(min-width: 960px) 50vw, 100vw';

    image.className = 'image__img image__img--lqip';
    image.src = getCdnImageUrl(source, placeholderWidth);
    image.srcset = getResponsiveSrcset(source, widths);
    image.sizes = sizes;
    image.alt = preview.alt || media.alt || '';
    image.width = preview.width || media.width || 1;
    image.height = preview.height || media.height || 1;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.setAttribute('data-al-progressive-image', '');
    image.removeAttribute('data-al-progressive-bound');
  }

  function createMainMedia(media, active) {
    const wrapper = mainMediaTemplate
      ? mainMediaTemplate.cloneNode(true)
      : document.createElement('div');
    const preview = media.preview_image || media;
    const aspectRatio = wrapper.dataset.aspectRatio || 'natural';

    wrapper.className = `product__media-item${active ? '' : ' hidden'}`;
    wrapper.dataset.mediaItemId = String(media.id);
    wrapper.dataset.aspectRatio = aspectRatio;
    wrapper.setAttribute('data-product-media-wrapper', '');
    wrapper.removeAttribute('data-al-media-placeholder');
    wrapper.innerHTML = `
      <div class="media media--aspect-ratio-${aspectRatio}" data-media-id="${media.id}" data-media-type="image">
        <a><div class="image js-enabled image--style-disabled"><img></div></a>
      </div>
    `;

    const image = wrapper.querySelector('img');
    const imageWrapper = wrapper.querySelector('.image');
    if (aspectRatio === 'natural' && imageWrapper && preview.width && preview.height) {
      imageWrapper.style.paddingTop = `${(preview.height / preview.width) * 100}%`;
    }
    prepareImage(image, Object.assign({}, media, { preview_image: preview }), false);
    image.loading = active ? 'eager' : 'lazy';
    image.fetchPriority = active ? 'high' : 'auto';
    return wrapper;
  }

  function createThumbnail(media, active, index) {
    const item = thumbnailTemplate
      ? thumbnailTemplate.cloneNode(true)
      : document.createElement('li');

    item.className = 'product-thumbnails__item animation-fade-up-reveal';
    item.hidden = false;
    item.removeAttribute('data-al-media-placeholder');
    item.innerHTML = `
      <button type="button" class="product-thumbnails__item-link${active ? ' active' : ''}" data-thumbnail-id="${media.id}" data-product-thumbnail aria-label="Image thumbnail ${index + 1}">
        <div class="image js-enabled image--style-disabled product-thumbnails__item-image"><img></div>
      </button>
    `;
    prepareImage(item.querySelector('img'), media, true);
    return item;
  }

  function buildMediaGroup(groupKey, media, activeMediaId) {
    const cacheKey = `${groupKey}:${activeMediaId}`;
    if (mediaGroupCache.has(cacheKey)) return mediaGroupCache.get(cacheKey);

    const main = document.createDocumentFragment();
    const thumbnails = document.createDocumentFragment();
    const selectedId = media.some((item) => String(item.id) === activeMediaId)
      ? activeMediaId
      : String(media[0].id);

    media.forEach((item, index) => {
      const active = String(item.id) === selectedId;
      main.appendChild(createMainMedia(item, active));
      thumbnails.appendChild(createThumbnail(item, active, index));
    });

    const result = {
      mainHtml: Array.from(main.childNodes).map((node) => node.outerHTML).join(''),
      thumbnailHtml: Array.from(thumbnails.childNodes).map((node) => node.outerHTML).join(''),
      mediaCount: media.length,
      selectedId
    };
    mediaGroupCache.set(cacheKey, result);
    return result;
  }

  function renderMediaGroup(product, variant, groupKey, media, sequence) {
    if (sequence !== renderSequence || !media.length) return false;
    if (media.some((item) => item.media_type !== 'image')) return false;

    const gallery = product.querySelector('.product__media');
    const mediaContainer = product.querySelector(mediaContainerSelector);
    if (!gallery || !mediaContainer) return false;

    const activeMediaId = getMediaId(variant);
    const rendered = buildMediaGroup(groupKey, media, activeMediaId);
    gallery.querySelectorAll(`${mediaWrapperSelector}, .placeholder-image`).forEach((item) => item.remove());
    gallery.insertAdjacentHTML('beforeend', rendered.mainHtml);

    let thumbnails = product.querySelector('.product-thumbnails');
    if (rendered.mediaCount > 1) {
      if (!thumbnails) {
        thumbnails = document.createElement('ul');
        thumbnails.className = 'product-thumbnails';
        thumbnails.setAttribute('data-product-thumbnails', '');
        const viewInSpaceButton = mediaContainer.querySelector('[data-in-your-space]');
        (viewInSpaceButton || gallery).insertAdjacentElement('afterend', thumbnails);
      }
      thumbnails.innerHTML = rendered.thumbnailHtml;
    } else if (thumbnails) {
      thumbnails.remove();
    }

    bindProgressiveImages(mediaContainer);
    mediaContainer.querySelectorAll(
      `${mediaWrapperSelector}:not(.hidden) ${progressiveImageSelector}, .product-thumbnails ${progressiveImageSelector}`
    ).forEach(upgradeResponsiveImage);
    lastRenderedVariantId = String(variant.id);
    lastRenderedGroupKey = groupKey;

    document.dispatchEvent(new CustomEvent('al:pdp-media-refreshed', {
      detail: {
        variant,
        mediaContainer,
        fastPath: true
      }
    }));

    return true;
  }

  function tryFastMediaRefresh(product, variant, sequence) {
    if (!canAttemptFastPath(product)) return Promise.resolve(false);

    return loadProductMediaIndex().then((index) => {
      if (!index || sequence !== renderSequence) return false;

      const groupValue = variant.options && variant.options[index.colourOptionIndex];
      const groupKey = String(groupValue == null ? '' : groupValue);
      const media = index.groups.get(groupKey);

      if (!media || !media.length) return false;
      if (lastRenderedGroupKey === groupKey && hasMedia(product, getMediaId(variant))) {
        switchImage(product, getMediaId(variant));
        lastRenderedVariantId = String(variant.id);
        return true;
      }
      return renderMediaGroup(product, variant, groupKey, media, sequence);
    });
  }

  function cacheMediaState(variantId, mediaContainer, lightbox) {
    if (!variantId || !mediaContainer || mediaCache.has(String(variantId))) return;

    const cachedMediaContainer = mediaContainer.cloneNode(true);
    cachedMediaContainer.classList.remove('is-loading');
    delete cachedMediaContainer.dataset.alPdpMediaBound;
    cachedMediaContainer.querySelectorAll('[data-al-progressive-bound]').forEach((image) => {
      image.removeAttribute('data-al-progressive-bound');
    });

    const cachedLightbox = normalizeLightboxClone(lightbox);

    mediaCache.set(String(variantId), {
      mediaContainerHtml: cachedMediaContainer.outerHTML,
      lightboxHtml: cachedLightbox ? cachedLightbox.outerHTML : ''
    });
  }

  function normalizeLightboxClone(lightbox) {
    if (!lightbox) return null;

    const clone = lightbox.cloneNode(true);
    const sliderElement = clone.querySelector('[data-slider]');
    const flickitySlider = sliderElement && sliderElement.querySelector('.flickity-slider');

    if (sliderElement && flickitySlider) {
      const slides = Array.from(flickitySlider.children);
      sliderElement.innerHTML = '';
      slides.forEach((slide) => sliderElement.appendChild(slide));
    }

    if (sliderElement) {
      sliderElement.classList.remove('flickity-enabled', 'flickity-rtl');
      sliderElement.removeAttribute('tabindex');
      sliderElement.removeAttribute('style');
    }

    clone.classList.remove('active', 'visible');
    clone.removeAttribute('data-al-lightbox-managed');
    clone.querySelectorAll('[data-slide]').forEach((slide) => {
      slide.hidden = false;
      slide.classList.remove('is-selected', 'zoom');
      slide.removeAttribute('aria-hidden');
      slide.removeAttribute('style');
    });

    return clone;
  }

  function createElementFromHtml(html) {
    if (!html) return null;

    const template = document.createElement('template');
    template.innerHTML = html.trim();

    return template.content.firstElementChild;
  }

  function getCachedMediaState(variantId) {
    const cached = mediaCache.get(String(variantId));
    if (!cached) return null;

    return {
      mediaContainer: createElementFromHtml(cached.mediaContainerHtml),
      lightbox: createElementFromHtml(cached.lightboxHtml)
    };
  }

  function ensureThemeSwitchTargets(product, variant) {
    const mediaId = getMediaId(variant);
    if (!mediaId) return;

    const mainGallery = product.querySelector('.product__media');
    if (mainGallery && !mainGallery.querySelector(`[data-media-item-id="${mediaId}"]`)) {
      const aspectRatio = mainGallery.querySelector('[data-aspect-ratio]')?.dataset.aspectRatio || 'natural';
      const mediaType = variant.featured_media.media_type || 'image';
      const wrapper = document.createElement('div');

      wrapper.className = 'product__media-item hidden';
      wrapper.dataset.mediaItemId = mediaId;
      wrapper.dataset.aspectRatio = aspectRatio;
      wrapper.setAttribute('data-product-media-wrapper', '');
      wrapper.setAttribute('data-al-media-placeholder', 'true');
      wrapper.innerHTML = `<div class="media media--aspect-ratio-${aspectRatio}" data-media-id="${mediaId}" data-media-type="${mediaType}"></div>`;

      mainGallery.appendChild(wrapper);
    }

    const thumbnails = product.querySelector('.product-thumbnails');
    if (thumbnails && !thumbnails.querySelector(`[data-thumbnail-id="${mediaId}"]`)) {
      const item = document.createElement('li');
      item.className = 'product-thumbnails__item';
      item.hidden = true;
      item.setAttribute('data-al-media-placeholder', 'true');
      item.innerHTML = `
        <button
          type="button"
          class="product-thumbnails__item-link"
          data-thumbnail-id="${mediaId}"
          data-product-thumbnail
          aria-label="Selected variant image"
        ></button>
      `;
      thumbnails.appendChild(item);
    }
  }

  function upgradeResponsiveImage(image) {
    if (!image || image.dataset.alResponsiveRequested === 'true') return;
    image.dataset.alResponsiveRequested = 'true';

    if (image.srcset) return;

    const thumbnail = !!image.closest(thumbnailSelector);
    const widths = thumbnail ? [70, 140, 210] : responsiveWidths;
    image.sizes = thumbnail ? '70px' : '(min-width: 960px) 50vw, 100vw';
    image.srcset = getResponsiveSrcset(image.currentSrc || image.src, widths);
  }

  function switchImage(product, mediaId) {
    const nextMedia = product.querySelector(`${mediaWrapperSelector}[data-media-item-id="${mediaId}"]`);
    if (!nextMedia) return;

    product.querySelectorAll(mediaWrapperSelector).forEach((media) => {
      media.classList.toggle('hidden', media !== nextMedia);
    });

    product.querySelectorAll(thumbnailSelector).forEach((thumbnail) => {
      thumbnail.classList.toggle('active', thumbnail.dataset.thumbnailId === mediaId);
    });

    const viewInSpaceButton = product.querySelector('[data-in-your-space]');
    const mediaElement = nextMedia.querySelector('.media');
    upgradeResponsiveImage(nextMedia.querySelector(progressiveImageSelector));
    if (
      viewInSpaceButton &&
      mediaElement &&
      mediaElement.dataset.mediaType === 'model'
    ) {
      viewInSpaceButton.setAttribute('data-shopify-model3d-id', mediaElement.dataset.mediaId);
    }

    pauseActiveInteractiveMedia(mediaElement);
  }

  function bindMediaContainer(mediaContainer) {
    if (!mediaContainer || mediaContainer.dataset.alPdpMediaBound === 'true') return;

    mediaContainer.dataset.alPdpMediaBound = 'true';
    mediaContainer.addEventListener('click', (event) => {
      const thumbnail = event.target.closest(thumbnailSelector);
      if (!thumbnail || !mediaContainer.contains(thumbnail)) return;

      event.preventDefault();

      const product = mediaContainer.closest(productSelector);
      if (!product || !thumbnail.dataset.thumbnailId) return;

      switchImage(product, thumbnail.dataset.thumbnailId);
    });
  }

  function bindProgressiveImages(container) {
    if (!container) return;

    container.querySelectorAll(progressiveImageSelector).forEach((image) => {
      const markLoaded = () => {
        image.classList.add('is-loaded');
      };

      if (image.complete && image.naturalWidth > 0) {
        markLoaded();
      } else if (image.dataset.alProgressiveBound !== 'true') {
        image.addEventListener('load', markLoaded, { once: true });
        image.addEventListener('lazyloaded', markLoaded, { once: true });
      }

      image.dataset.alProgressiveBound = 'true';
    });
  }

  function setManagedLightboxSlide(index) {
    if (!managedLightbox) return;

    const slides = Array.from(managedLightbox.querySelectorAll('[data-slide]'));
    const navItems = Array.from(managedLightbox.querySelectorAll('[data-nav-item]'));
    const previousButton = managedLightbox.querySelector('[data-prev]');
    const nextButton = managedLightbox.querySelector('[data-next]');

    if (!slides.length) return;

    managedLightboxIndex = Math.max(0, Math.min(Number(index) || 0, slides.length - 1));
    slides.forEach((slide, slideIndex) => {
      slide.hidden = slideIndex !== managedLightboxIndex;
      if (slideIndex !== managedLightboxIndex) resetManagedLightboxZoom(slide);
    });
    navItems.forEach((item, navIndex) => {
      item.classList.toggle('active', navIndex === managedLightboxIndex);
    });

    const activeNavItem = navItems[managedLightboxIndex];
    if (activeNavItem) {
      activeNavItem.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
    }

    if (previousButton) {
      previousButton.hidden = slides.length < 2;
      previousButton.disabled = managedLightboxIndex === 0;
    }
    if (nextButton) {
      nextButton.hidden = slides.length < 2;
      nextButton.disabled = managedLightboxIndex === slides.length - 1;
    }
  }

  function resetManagedLightboxZoom(slide) {
    const slides = slide
      ? [slide]
      : managedLightbox
        ? Array.from(managedLightbox.querySelectorAll('[data-slide]'))
        : [];

    slides.forEach((item) => {
      item.classList.remove('zoom');
      item.style.transform = 'translate3d(0px, 0px, 0) scale(1)';
    });
  }

  function lockManagedLightboxScroll() {
    lockedDocumentOverflow = document.documentElement.style.overflow;
    lockedBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  function unlockManagedLightboxScroll() {
    document.documentElement.style.overflow = lockedDocumentOverflow;
    document.body.style.overflow = lockedBodyOverflow;
  }

  function openManagedLightbox(mediaId, opener) {
    if (!managedLightbox) return;

    const slides = Array.from(managedLightbox.querySelectorAll('[data-slide]'));
    const selectedIndex = slides.findIndex((slide) => String(slide.dataset.id) === String(mediaId));
    managedLightboxLastFocus = opener || document.activeElement;
    setManagedLightboxSlide(selectedIndex < 0 ? 0 : selectedIndex);
    managedLightbox.classList.add('active');
    managedLightbox.setAttribute('aria-hidden', 'false');
    lockManagedLightboxScroll();

    requestAnimationFrame(() => {
      if (!managedLightbox) return;
      managedLightbox.classList.add('visible');
      const closeButton = managedLightbox.querySelector('[data-close]');
      if (closeButton) closeButton.focus();
    });
  }

  function closeManagedLightbox() {
    if (!managedLightbox || !managedLightbox.classList.contains('active')) return;

    resetManagedLightboxZoom();
    managedLightbox.classList.remove('visible');
    managedLightbox.setAttribute('aria-hidden', 'true');
    unlockManagedLightboxScroll();

    const lightboxToClose = managedLightbox;
    window.setTimeout(() => {
      if (lightboxToClose === managedLightbox) lightboxToClose.classList.remove('active');
    }, 300);

    const focusTarget = managedLightboxLastFocus;
    managedLightboxLastFocus = null;
    window.setTimeout(() => {
      if (focusTarget && focusTarget.isConnected) focusTarget.focus();
    }, 300);
  }

  function toggleManagedLightboxZoom(slide, event) {
    const zoomed = slide.classList.contains('zoom');
    resetManagedLightboxZoom(slide);
    if (zoomed) return;

    const wrapper = managedLightbox.querySelector('.lightbox__images-wrapper');
    const slider = managedLightbox.querySelector('[data-slider]');
    const x = event.clientX || slide.clientWidth / 2;
    const y = (event.clientY || slide.clientHeight / 2) + (wrapper ? wrapper.scrollTop : 0) - (slider ? slider.offsetTop : 0);
    const xDelta = (x - slide.clientWidth / 2) * -1;
    const yDelta = (y - slide.clientHeight / 2) * -1;

    slide.classList.add('zoom');
    slide.style.transform = `translate3d(${xDelta}px, ${yDelta}px, 0) scale(2)`;
  }

  function destroyThemeLightboxSlider(lightbox) {
    const sliderElement = lightbox && lightbox.querySelector('[data-slider]');
    const flickityUrl = window.flu && window.flu.chunks && window.flu.chunks.flickity;
    if (!sliderElement || !flickityUrl || !sliderElement.classList.contains('flickity-enabled')) {
      return Promise.resolve();
    }

    return import(flickityUrl)
      .then(({ Flickity }) => {
        const slider = Flickity.data(sliderElement);
        if (slider) slider.destroy();
      })
      .catch((error) => {
        console.warn('[PDP lightbox refresh]', error);
      });
  }

  function replaceLightbox(product, newLightbox) {
    const currentLightbox = product && product.querySelector(lightboxSelector);
    if (!currentLightbox || !newLightbox) return Promise.resolve();

    return destroyThemeLightboxSlider(currentLightbox).then(() => {
      if (!currentLightbox.isConnected) return;

      currentLightbox.innerHTML = newLightbox.innerHTML;
      currentLightbox.setAttribute('role', newLightbox.getAttribute('role') || 'dialog');
      currentLightbox.setAttribute('aria-modal', 'true');
      currentLightbox.setAttribute('aria-label', newLightbox.getAttribute('aria-label') || 'Product images');
      currentLightbox.setAttribute('aria-hidden', 'true');
      currentLightbox.classList.remove('active', 'visible');
      currentLightbox.dataset.alLightboxManaged = 'true';
      managedLightbox = currentLightbox;
      managedLightboxIndex = 0;
      setManagedLightboxSlide(0);
      bindProgressiveImages(currentLightbox);
    });
  }

  function handleManagedLightboxClick(event) {
    if (!managedLightbox || !managedLightbox.isConnected) return;

    const opener = event.target.closest('[data-open]');
    const product = getProductContainer();
    if (opener && product && product.contains(opener) && !managedLightbox.contains(opener)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openManagedLightbox(opener.dataset.open, opener);
      return;
    }

    if (!managedLightbox.contains(event.target)) return;

    const closeButton = event.target.closest('[data-close]');
    const previousButton = event.target.closest('[data-prev]');
    const nextButton = event.target.closest('[data-next]');
    const navItem = event.target.closest('[data-nav-item]');
    const slide = event.target.closest('[data-slide]');

    if (!closeButton && !previousButton && !nextButton && !navItem && !slide) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (closeButton) closeManagedLightbox();
    else if (previousButton) setManagedLightboxSlide(managedLightboxIndex - 1);
    else if (nextButton) setManagedLightboxSlide(managedLightboxIndex + 1);
    else if (navItem) setManagedLightboxSlide(navItem.dataset.index);
    else if (slide) toggleManagedLightboxZoom(slide, event);
  }

  function handleManagedLightboxKeydown(event) {
    if (!managedLightbox || !managedLightbox.classList.contains('active')) return;

    if (event.key === 'Escape' || event.keyCode === 27) {
      event.preventDefault();
      event.stopImmediatePropagation();
      closeManagedLightbox();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setManagedLightboxSlide(managedLightboxIndex - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setManagedLightboxSlide(managedLightboxIndex + 1);
    } else if (event.key === 'Tab') {
      const focusable = Array.from(managedLightbox.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function handleManagedLightboxTouchStart(event) {
    if (!managedLightbox || !managedLightbox.contains(event.target) || event.touches.length !== 1) return;
    managedLightboxTouchStartX = event.touches[0].clientX;
  }

  function handleManagedLightboxTouchEnd(event) {
    if (managedLightboxTouchStartX === null || !managedLightbox || !managedLightbox.contains(event.target)) return;
    const endX = event.changedTouches && event.changedTouches[0] && event.changedTouches[0].clientX;
    const delta = typeof endX === 'number' ? endX - managedLightboxTouchStartX : 0;
    managedLightboxTouchStartX = null;
    if (Math.abs(delta) < 50) return;
    setManagedLightboxSlide(managedLightboxIndex + (delta < 0 ? 1 : -1));
  }

  function fetchMediaContainer(variant, signal) {
    const product = getProductContainer();
    if (!product) return Promise.resolve(null);

    const sectionId = product.dataset.sectionId;
    if (!sectionId) return Promise.resolve(null);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    url.searchParams.set('section_id', sectionId);

    return fetch(url.toString(), {
      credentials: 'same-origin',
      signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newMediaContainer =
          doc.querySelector(`[data-section-id="${sectionId}"] ${mediaContainerSelector}`) ||
          doc.querySelector(mediaContainerSelector);
        const newLightbox =
          doc.querySelector(`[data-section-id="${sectionId}"] ${lightboxSelector}`) ||
          doc.querySelector(lightboxSelector);

        if (!newMediaContainer) {
          throw new Error('Section response did not include a media container');
        }

        cacheMediaState(variant.id, newMediaContainer, newLightbox);

        return {
          mediaContainer: newMediaContainer,
          lightbox: newLightbox
        };
      });
  }

  function getPrimaryMediaWrapper(mediaContainer, variant) {
    const mediaId = getMediaId(variant);

    return (
      mediaId && mediaContainer.querySelector(`${mediaWrapperSelector}[data-media-item-id="${mediaId}"]`)
    ) || mediaContainer.querySelector(`${mediaWrapperSelector}:not(.hidden)`) || mediaContainer.querySelector(mediaWrapperSelector);
  }

  function getPreloadImageSource(image) {
    return {
      src: image.currentSrc || image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
      sizes: image.getAttribute('sizes') || ''
    };
  }

  function waitForPrimaryImage(newMediaContainer, variant) {
    const primaryMedia = getPrimaryMediaWrapper(newMediaContainer, variant);
    const mediaElement = primaryMedia && primaryMedia.querySelector('.media');

    if (!mediaElement || mediaElement.dataset.mediaType !== 'image') {
      return Promise.resolve();
    }

    const image = primaryMedia.querySelector('.image__img');
    const source = image && getPreloadImageSource(image);

    if (!source || (!source.src && !source.srcset)) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let settled = false;
      const preloadImage = new Image();
      const timer = window.setTimeout(() => resolveOnce(), primaryImageDecodeTimeout);

      function resolveOnce() {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve();
      }

      preloadImage.decoding = 'async';
      if (source.sizes) preloadImage.sizes = source.sizes;
      if (source.srcset) preloadImage.srcset = source.srcset;
      if (source.src) preloadImage.src = source.src;

      const decode = preloadImage.decode ? preloadImage.decode() : null;

      if (decode) {
        decode.then(resolveOnce).catch(resolveOnce);
      } else {
        preloadImage.onload = resolveOnce;
        preloadImage.onerror = resolveOnce;
      }
    });
  }

  function replaceMediaContainer(product, newMediaContainer, newLightbox, variant) {
    const currentMediaContainer = product && product.querySelector(mediaContainerSelector);
    if (!currentMediaContainer || !newMediaContainer) return Promise.resolve();

    return waitForPrimaryImage(newMediaContainer, variant).then(() => {
      if (
        lastRequestedVariantId &&
        lastRequestedVariantId !== String(variant.id)
      ) {
        return;
      }

      if (!currentMediaContainer.isConnected) return;

      currentMediaContainer.replaceWith(newMediaContainer);
      bindMediaContainer(newMediaContainer);
      bindProgressiveImages(newMediaContainer);
      initInteractiveMedia(newMediaContainer);
      lastRenderedVariantId = String(variant.id);

      return replaceLightbox(product, newLightbox).then(() => {
        document.dispatchEvent(new CustomEvent('al:pdp-media-refreshed', {
          detail: {
            variant,
            mediaContainer: newMediaContainer
          }
        }));
      });
    });
  }

  function pauseActiveInteractiveMedia(nextMedia) {
    if (!activeInteractiveMedia || activeInteractiveMedia.container === nextMedia) return;

    if (activeInteractiveMedia.player) {
      if (activeInteractiveMedia.type === 'video') {
        activeInteractiveMedia.player.pause();
      } else if (activeInteractiveMedia.type === 'external_video') {
        activeInteractiveMedia.player.pauseVideo();
      }
    } else if (activeInteractiveMedia.viewer) {
      activeInteractiveMedia.container.classList.remove('model-active');
      activeInteractiveMedia.viewer.pause();
    }

    activeInteractiveMedia = null;
  }

  function initInteractiveMedia(mediaContainer) {
    const elements = Array.from(mediaContainer.querySelectorAll('[data-interactive]'));
    if (!elements.length || !window.Shopify || !window.Shopify.loadFeatures) return;

    // THL 2026-09-18: 3D/AR disabled; load only the video player.
    window.Shopify.loadFeatures([
      {
        name: 'video-ui',
        version: '1.0'
      }
    ], () => {
      elements.forEach(initInteractiveElement);
    });
  }

  function initInteractiveElement(element) {
    if (element.dataset.alInteractiveReady === 'true') return;

    const mediaId = element.dataset.mediaId;
    const mediaType = element.dataset.mediaType;
    const media = element.children[0];

    // THL: model media remains disabled even if another app loads its library.
    if (!mediaId || !mediaType || !media || mediaType === 'model') return;

    const instance = {
      id: mediaId,
      type: mediaType,
      container: element,
      media
    };

    if (mediaType === 'video' && window.Shopify && window.Shopify.Plyr) {
      instance.player = new window.Shopify.Plyr(media, {
        loop: {
          active: element.dataset.loop === 'true'
        }
      });

      instance.player.on('playing', () => {
        pauseActiveInteractiveMedia(element);
        activeInteractiveMedia = instance;
      });
    } else if (mediaType === 'external_video' && window.YT && window.YT.Player) {
      instance.player = new window.YT.Player(media);
      instance.player.addEventListener('onStateChange', (event) => {
        if (event.data === 1) {
          pauseActiveInteractiveMedia(element);
          activeInteractiveMedia = instance;
        }
      });
    } else if (mediaType === 'model' && window.Shopify && window.Shopify.ModelViewerUI) {
      const modelViewer = element.querySelector('model-viewer');
      const poster = element.querySelector('.model-poster');

      if (!modelViewer) return;

      instance.viewer = new window.Shopify.ModelViewerUI(modelViewer);

      if (poster) {
        poster.addEventListener('click', (event) => {
          event.preventDefault();
          pauseActiveInteractiveMedia(element);
          instance.viewer.play();
          element.classList.add('model-active');
          activeInteractiveMedia = instance;
          setTimeout(() => modelViewer.focus(), 300);
        });
      }
    } else {
      return;
    }

    element.dataset.alInteractiveReady = 'true';
  }

  function refreshMediaForVariant(variant) {
    const product = getProductContainer();
    if (!product) return;

    const mediaId = getMediaId(variant);
    if (!mediaId) return;
    lastRequestedVariantId = String(variant.id);

    bindMediaContainer(product.querySelector(mediaContainerSelector));

    /* Bedding Clearance start */
    if (!isBeddingClearanceProduct(product) && hasMedia(product, mediaId)) {
      lastRenderedVariantId = String(variant.id);
      return;
    }
    /* Bedding Clearance end */

    /* Bedding Clearance start */
    if (!isBeddingClearanceProduct(product)) {
      ensureThemeSwitchTargets(product, variant);
    }
    /* Bedding Clearance end */

    const mediaContainer = product.querySelector(mediaContainerSelector);
    if (!mediaContainer) return;

    cacheMediaState(
      lastRenderedVariantId || getCurrentVariantId(product),
      mediaContainer,
      product.querySelector(lightboxSelector)
    );

    const cachedMediaState = getCachedMediaState(variant.id);
    if (cachedMediaState) {
      mediaContainer.classList.add('is-loading');

      requestAnimationFrame(() => {
        replaceMediaContainer(product, cachedMediaState.mediaContainer, cachedMediaState.lightbox, variant);
      });

      return;
    }

    if (activeRequest) {
      activeRequest.abort();
    }

    activeRequest = new AbortController();
    const request = activeRequest;

    mediaContainer.classList.add('is-loading');

    fetchMediaContainer(variant, request.signal)
      .then((newMediaState) => {
        if (lastRequestedVariantId !== String(variant.id)) return;

        const currentProduct = getProductContainer();
        return replaceMediaContainer(
          currentProduct,
          newMediaState.mediaContainer,
          newMediaState.lightbox,
          variant
        );
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.warn('[PDP media refresh]', error);
        }
      })
      .finally(() => {
        const currentProduct = getProductContainer();
        const currentMediaContainer = currentProduct && currentProduct.querySelector(mediaContainerSelector);
        currentMediaContainer && currentMediaContainer.classList.remove('is-loading');

        if (activeRequest === request) {
          activeRequest = null;
        }
      });
  }

  function initializeProductMedia() {
    const product = getProductContainer();
    const mediaContainer = product && product.querySelector(mediaContainerSelector);

    bindMediaContainer(mediaContainer);
    bindProgressiveImages(mediaContainer);

    lastRenderedVariantId = getCurrentVariantId(product);
    cacheMediaState(lastRenderedVariantId, mediaContainer, product && product.querySelector(lightboxSelector));
    mainMediaTemplate = product && product.querySelector(`${mediaWrapperSelector}:not([data-al-media-placeholder])`);
    thumbnailTemplate = product && product.querySelector('.product-thumbnails__item:not([data-al-media-placeholder])');
    prefetchProductMediaIndex();
  }

  function handleVariantMediaChange(variant) {
    const product = getProductContainer();
    if (!product) return;

    const sequence = ++renderSequence;
    lastRequestedVariantId = String(variant.id);

    // The theme switches the featured image synchronously after dispatching
    // product:variant-change. Give it a valid target before the async fast path
    // or Section fallback starts, otherwise the native switchImage throws.
    if (!isBeddingClearanceProduct(product) && !hasMedia(product, getMediaId(variant))) {
      ensureThemeSwitchTargets(product, variant);
    }

    if (activeRequest) {
      activeRequest.abort();
      activeRequest = null;
    }

    tryFastMediaRefresh(product, variant, sequence)
      .then((rendered) => {
        if (rendered || sequence !== renderSequence) return;
        refreshMediaForVariant(variant);
      })
      .catch((error) => {
        if (sequence !== renderSequence) return;
        console.warn('[PDP media fast path]', error);
        refreshMediaForVariant(variant);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeProductMedia);
  } else {
    initializeProductMedia();
  }

  document.addEventListener('product:variant-change', (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;

    handleVariantMediaChange(variant);
    Promise.resolve().then(() => updateQuickPurchaseImage(getProductContainer(), variant));
  });

  document.addEventListener('click', handleManagedLightboxClick, true);
  document.addEventListener('keydown', handleManagedLightboxKeydown, true);
  document.addEventListener('touchstart', handleManagedLightboxTouchStart, { passive: true });
  document.addEventListener('touchend', handleManagedLightboxTouchEnd, { passive: true });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target.matches(productSelector) && !event.target.querySelector(productSelector)) return;

    renderSequence += 1;
    if (activeRequest) {
      activeRequest.abort();
      activeRequest = null;
    }
    if (managedLightbox && managedLightbox.classList.contains('active')) {
      unlockManagedLightboxScroll();
    }
    managedLightbox = null;
    managedLightboxLastFocus = null;
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target.matches(productSelector) && !event.target.querySelector(productSelector)) return;

    renderSequence += 1;
    if (activeRequest) {
      activeRequest.abort();
      activeRequest = null;
    }
    productDataPromise = null;
    productMediaIndex = null;
    productDataFailed = false;
    lastRenderedGroupKey = '';
    managedLightbox = null;
    managedLightboxIndex = 0;
    managedLightboxLastFocus = null;
    mediaCache.clear();
    mediaGroupCache.clear();
    initializeProductMedia();
  });
})();
